import { useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import BrandLogo from "@/components/BrandLogo";
import { parsePhoneToE164 } from "@/lib/phoneFormat";
import PhoneCountryNationalInput from "@/components/PhoneCountryNationalInput";
import { goBackOr } from "@/lib/goBackOr";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [phoneCountryId, setPhoneCountryId] = useState("FR");
  const [phoneNational, setPhoneNational] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [certifiesProfessional, setCertifiesProfessional] = useState(false);
  const [acceptsLegal, setAcceptsLegal] = useState(false);
  const [loading, setLoading] = useState(false);
  const registerSubmitLock = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (registerSubmitLock.current || loading) return;
    const company = companyName.trim();
    const prenom = firstName.trim();
    const nomFamille = lastName.trim();
    if (!prenom) return toast.error("Le prénom est obligatoire");
    if (!nomFamille) return toast.error("Le nom est obligatoire");
    if (!email.trim()) return toast.error("L’e-mail est obligatoire");
    if (!password || password.length < 6) return toast.error("Mot de passe trop court (min. 6 caractères)");
    if (!certifiesProfessional) {
      return toast.error("Vous devez certifier exercer une activité professionnelle");
    }
    if (!acceptsLegal) return toast.error("Vous devez accepter les CGU et la politique de confidentialité");
    let telephone;
    try {
      telephone = parsePhoneToE164({ countryId: phoneCountryId, value: phoneNational });
    } catch (err) {
      return toast.error(err.message);
    }
    // `nom` reste envoyé pour compatibilité (profils.name/nom),
    // mais les champs source sont bien distincts : first_name / last_name.
    const nom = `${prenom} ${nomFamille}`.trim();
    registerSubmitLock.current = true;
    setLoading(true);
    try {
      await register({
        email: email.trim().toLowerCase(),
        password,
        nom,
        first_name: prenom,
        last_name: nomFamille,
        telephone,
        company_name: company || null,
        country: null,
        charter_accepted: true,
        professional_certified: true,
      });
      toast.success("Compte créé — accès immédiat à votre espace.");
      const ret = location.state?.from;
      if (ret?.pathname) {
        navigate(`${ret.pathname}${ret.search || ""}`, { replace: true });
      } else {
        navigate("/marketplace", { replace: true });
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "";
      if (status === 409) {
        toast.info("Compte déjà existant — connectez-vous ou utilisez « mot de passe oublié ».");
        navigate("/login", { state: location.state });
        return;
      }
      toast.error(detail || "Erreur à la création");
    } finally {
      setLoading(false);
      registerSubmitLock.current = false;
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Seo
        title="Créer un compte"
        description="Inscription RdvMarket : e-mail, téléphone, nom/prénom — accès immédiat."
        canonicalPath="/register"
        robots="noindex, nofollow"
      />
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/7794060/pexels-photo-7794060.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          className="absolute inset-0 w-full h-full object-cover"
          alt=""
        />
        <div className="absolute inset-0 bg-[#0B0D12]/75 mix-blend-multiply" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <BrandLogo
            to="/"
            size="md"
            showWordmark
            wordmarkClassName="font-black text-2xl tracking-[-0.03em] text-white"
          />
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-widest opacity-80 mb-4">Inscription express</div>
            <h2 className="font-display font-black text-4xl leading-tight mb-4">E-mail, téléphone, identité.</h2>
            <p className="text-white/70 mb-6">
              C’est tout : vous accédez tout de suite à la marketplace et à la publication. Aucun dossier KYC à l’inscription.
              Stripe vérifiera votre carte à l’achat (abonnement) et vos infos bancaires au premier encaissement vendeur.
            </p>
            <ul className="space-y-2 text-sm text-white/90 list-disc pl-4">
              <li>Pas de pièces justificatives à l’inscription</li>
              <li>Publication immédiate des dossiers</li>
              <li>Encaissement : vérification légale via Stripe Connect</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-8 sm:p-8">
        <div className="w-full min-w-0 max-w-md">
          <button
            type="button"
            className="mb-6 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#2D7FF9]"
            onClick={() => goBackOr(navigate)}
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-2">Créer un compte</h1>
          <p className="text-sm text-slate-500 mb-8">
            E-mail, téléphone, nom/prénom — accès immédiat. Société ou organisation : optionnel. Choisissez un mot de passe pour sécuriser votre compte.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="firstName" required>
                  Prénom
                </Label>
                <Input
                  id="firstName"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="h-11 mt-1"
                  autoComplete="given-name"
                  data-testid="register-firstname-input"
                />
              </div>
              <div>
                <Label htmlFor="lastName" required>
                  Nom
                </Label>
                <Input
                  id="lastName"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="h-11 mt-1"
                  autoComplete="family-name"
                  data-testid="register-lastname-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email" required>
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 mt-1"
                autoComplete="email"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label required>Téléphone</Label>
              <PhoneCountryNationalInput
                className="mt-1"
                countryId={phoneCountryId}
                onCountryIdChange={setPhoneCountryId}
                value={phoneNational}
                onChange={setPhoneNational}
                countryTestId="register-phone-country"
                inputTestId="register-phone-input"
              />
              <p className="mt-1 text-xs text-slate-500">
                Choisissez votre pays, puis le numéro local (sans indicatif). Hors liste : « Autre pays » et saisie
                +indicatif (ex. +212…).
              </p>
            </div>

            <div>
              <Label htmlFor="company">Nom de société / Organisation (optionnel)</Label>
              <Input
                id="company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="h-11 mt-1"
                autoComplete="organization"
                placeholder="Ma société SAS"
                data-testid="register-company-input"
              />
            </div>

            <div>
              <Label htmlFor="password" required>
                Mot de passe (min. 6 caractères)
              </Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 mt-1"
                autoComplete="new-password"
                data-testid="register-password-input"
              />
            </div>

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                certifiesProfessional ? "border-[#2D7FF9] bg-[#2D7FF9]/5" : "border-[#E5E7EB] hover:border-slate-300"
              }`}
              data-testid="professional-checkbox-label"
            >
              <input
                type="checkbox"
                checked={certifiesProfessional}
                onChange={(e) => setCertifiesProfessional(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#2D7FF9] cursor-pointer shrink-0"
                data-testid="professional-checkbox"
              />
              <span className="text-xs text-slate-700 leading-relaxed">
                Je certifie exercer une activité professionnelle et agir en tant que professionnel
              </span>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                acceptsLegal ? "border-[#2D7FF9] bg-[#2D7FF9]/5" : "border-[#E5E7EB] hover:border-slate-300"
              }`}
              data-testid="cgu-checkbox-label"
            >
              <input
                type="checkbox"
                checked={acceptsLegal}
                onChange={(e) => setAcceptsLegal(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#2D7FF9] cursor-pointer shrink-0"
                data-testid="cgu-checkbox"
              />
              <span className="text-xs text-slate-700 leading-relaxed">
                J&apos;accepte les{" "}
                <Link to="/cgu" target="_blank" className="text-[#2D7FF9] font-semibold hover:underline" data-testid="cgu-link">
                  CGU et la politique de confidentialité
                </Link>
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading || !certifiesProfessional || !acceptsLegal}
              className="btn-primary w-full h-11 rounded-md mt-2 inline-flex items-center justify-center gap-2"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                  Création…
                </>
              ) : (
                "Créer le compte"
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-600 mt-8">
            Déjà un compte ?{" "}
            <Link to="/login" state={location.state} className="text-[#2D7FF9] font-semibold hover:underline">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
